import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import api from '../utils/api';
import {
  BarChart3, FileText, CheckCircle, Clock, XCircle,
  TrendingUp, Calendar, Target, Award, Loader2, ArrowRight
} from 'lucide-react';

const StudentAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Fetch applications and decisions
      const [appsRes, decisionsRes] = await Promise.all([
        api.get('/applications'),
        api.get('/decisions/all').catch(() => ({ data: [] }))
      ]);
      
      const applications = appsRes.data || [];
      const decisions = decisionsRes.data || [];
      
      // Calculate analytics
      const statusCounts = {
        draft: 0,
        submitted: 0,
        under_review: 0,
        accepted: 0,
        denied: 0,
        waitlisted: 0,
        deferred: 0
      };
      
      let totalProgress = 0;
      const deadlines = [];
      
      applications.forEach(app => {
        const status = app.status || 'draft';
        if (statusCounts.hasOwnProperty(status)) {
          statusCounts[status]++;
        }
        totalProgress += (app.progress || 0);
        
        if (app.deadline) {
          deadlines.push({
            college: app.college_name || 'College',
            deadline: new Date(app.deadline),
            type: app.deadline_type || 'Regular'
          });
        }
      });
      
      const avgProgress = applications.length > 0 ? Math.round(totalProgress / applications.length) : 0;
      
      // Sort deadlines by date
      deadlines.sort((a, b) => a.deadline - b.deadline);
      const upcomingDeadlines = deadlines.filter(d => d.deadline > new Date()).slice(0, 5);
      
      // Calculate acceptance rate
      const totalDecisions = statusCounts.accepted + statusCounts.denied + statusCounts.waitlisted;
      const acceptanceRate = totalDecisions > 0 
        ? Math.round((statusCounts.accepted / totalDecisions) * 100) 
        : null;
      
      setAnalytics({
        total: applications.length,
        statusCounts,
        avgProgress,
        upcomingDeadlines,
        acceptanceRate,
        decisions
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const { total, statusCounts, avgProgress, upcomingDeadlines, acceptanceRate } = analytics || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">My Analytics</h1>
            <p className="text-gray-600">Track your application progress and outcomes</p>
          </div>
          <Link to="/dashboard">
            <Button variant="outline">
              <ArrowRight className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Applications</p>
                  <p className="text-3xl font-bold">{total || 0}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Submitted</p>
                  <p className="text-3xl font-bold">{statusCounts?.submitted || 0}</p>
                </div>
                <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Accepted</p>
                  <p className="text-3xl font-bold text-green-600">{statusCounts?.accepted || 0}</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Award className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg. Progress</p>
                  <p className="text-3xl font-bold">{avgProgress}%</p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Status Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                Application Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { key: 'draft', label: 'Draft', color: 'bg-gray-500' },
                  { key: 'submitted', label: 'Submitted', color: 'bg-blue-500' },
                  { key: 'under_review', label: 'Under Review', color: 'bg-yellow-500' },
                  { key: 'accepted', label: 'Accepted', color: 'bg-green-500' },
                  { key: 'waitlisted', label: 'Waitlisted', color: 'bg-purple-500' },
                  { key: 'denied', label: 'Denied', color: 'bg-red-500' },
                ].map(({ key, label, color }) => {
                  const count = statusCounts?.[key] || 0;
                  const percentage = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{label}</span>
                        <span className="text-sm text-gray-600">{count}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${color}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Deadlines */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-red-600" />
                Upcoming Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingDeadlines && upcomingDeadlines.length > 0 ? (
                <div className="space-y-3">
                  {upcomingDeadlines.map((deadline, index) => {
                    const daysUntil = Math.ceil((deadline.deadline - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{deadline.college}</p>
                          <p className="text-sm text-gray-600">{deadline.type} Decision</p>
                        </div>
                        <div className="text-right">
                          <Badge 
                            className={daysUntil <= 7 ? 'bg-red-100 text-red-700' : daysUntil <= 14 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}
                          >
                            {daysUntil} days
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            {deadline.deadline.toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No upcoming deadlines</p>
                  <p className="text-sm">Add deadline dates to your applications</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Acceptance Rate */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-emerald-600" />
                Your Success Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              {acceptanceRate !== null ? (
                <div className="text-center">
                  <div className="relative w-32 h-32 mx-auto mb-4">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        className="text-gray-200"
                        strokeWidth="10"
                        stroke="currentColor"
                        fill="transparent"
                        r="40"
                        cx="50"
                        cy="50"
                      />
                      <circle
                        className="text-emerald-500"
                        strokeWidth="10"
                        strokeDasharray={251.2}
                        strokeDashoffset={251.2 - (acceptanceRate / 100) * 251.2}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="40"
                        cx="50"
                        cy="50"
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold">{acceptanceRate}%</span>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    {statusCounts?.accepted || 0} accepted out of {(statusCounts?.accepted || 0) + (statusCounts?.denied || 0) + (statusCounts?.waitlisted || 0)} decisions
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No decisions yet</p>
                  <p className="text-sm">Your acceptance rate will appear after receiving decisions</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/applications" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  View All Applications
                </Button>
              </Link>
              <Link to="/decisions" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Decision Tracker
                </Button>
              </Link>
              <Link to="/colleges" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Target className="h-4 w-4 mr-2" />
                  Find More Colleges
                </Button>
              </Link>
              <Link to="/admission-chance" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Check Admission Chances
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StudentAnalytics;
